/**
 * Form Entries Component
 * =====================
 * 
 * Comprehensive component for viewing and managing form entries with advanced filtering
 * and responsive design. This component provides two main viewing modes: form type-based
 * and submitter-based, with dynamic rendering between desktop table and mobile card layouts.
 * 
 * Core Features:
 * - Dual filter modes: Form Type and Submitter
 * - Dynamic layout switching (table vs. cards) based on screen size
 * - Advanced filtering for individu  
  let updatedEntries  // Update pagination state
  const totalCount = result?.entries?.count ?? 0;
  const hasMoreEntries = newEntries.length >= currentPagination.limit && updatedEntries.length < totalCount;

  this.paginationState.update(state => ({
    ...state,
    totalCount: totalCount,
    hasMoreEntries: hasMoreEntries
  }));
  if (isInitialLoad) {
    // Initial load - replace all entries
    updatedEntries = newEntries;
  } else {
    // Additional load - append new entries to existing ones
    updatedEntries = [...currentEntries, ...newEntries];
  }

  // Update entries
  this.formEntries.set(updatedEntries);ds
 * - Pagination with configurable page sizes
 * - Sorting capabilities for all columns
 * - Responsive design with mobile-optimized action sheets
 * - Real-time data loading with reactive state management
 * 
 * Filter Modes:
 * - Form Type Mode: Browse entries by specific form types with field-level filtering
 * - Submitter Mode: View all forms submitted by a specific person
 * 
 * Layout Adaptations:
 * - Desktop: Full table with advanced filtering modal
 * - Mobile: Card-based layout with action sheet navigation
 * - Responsive: Automatic switching based on screen size
 * 
 * Advanced Filtering:
 * - Field-specific filters for each form type
 * - Multiple filter operations (equals, contains, greater than, less than)
 * - Dynamic column generation based on form metadata
 * - Real-time filter application with pagination reset
 * 
 * Technical Implementation:
 * - Signal-based reactive state management
 * - Computed properties for derived state
 * - Route parameter synchronization
 * - Service integration for data fetching
 * - Effect-based reactive updates
 * 
 * @author MacSafety Development Team
 * @version 2.0
 */

import { Component, OnInit, OnDestroy, computed, signal, effect, ViewChild } from '@angular/core';
import { FormService } from '../../../../services/form.service';
import { FormListResponse } from '../../../../models/form-entry-lists/form-list-response.model';
import { ActivatedRoute, Router } from '@angular/router';
import { FormModelService } from '../../../../services/form-model.service';
import { FormMetadata } from '../../../../models/form-registry';
import { ScreenSizeService } from '../../../../services/screen-size.service';
import { AllFormsBySubmitterListItem } from '../../../../models/forms/all-forms-by-submitter/all-forms-by-submitter-list-item.model';
import { DynamicTableComponent, TableColumn } from './entry-list/dynamic-table/dynamic-table.component';
import { ActionSheetController, IonActionSheet, IonModal } from '@ionic/angular';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map, distinctUntilChanged, filter } from 'rxjs/operators';
import { untracked } from '@angular/core';

type FilterMode = 'formType' | 'submitter';

interface PaginationState {
  offset: number;
  limit: number;
  totalCount: number;
  hasMoreEntries?: boolean;
}

interface FilterState {
  column: string;
  value: string;
  isActive: boolean;
  filterType: 'equals' | 'contains' | 'greaterThan' | 'lessThan' ;
}

interface SortState {
  column: string;
  direction: 'asc' | 'desc';
}

/**
 * Refactored FormEntriesComponent using modern Angular patterns
 * 
 * Key improvements:
 * - Uses signals for reactive state management
 * - Separates concerns into logical methods
 * - Cleaner route parameter handling
 * - Better type safety
 * - Reduced code duplication
 * - More predictable state updates
 */
@Component({
  selector: 'app-form-entries',
  templateUrl: './form-entries.component.html',
  styleUrls: ['./form-entries.component.css']
})
export class FormEntriesComponent implements OnInit {
  @ViewChild('filterActionSheet') filterActionSheet!: IonActionSheet;
  @ViewChild(DynamicTableComponent) dynamicTable!: DynamicTableComponent;
  @ViewChild('filterSortModal') filterSortModal!: IonModal;

  // ========================================
  // SIGNALS - REACTIVE STATE
  // ========================================
  
  // Core filter state
  filterMode = signal<FilterMode>('formType');
  selectedFormType = signal<string>('');
  selectedSubmitter = signal<string>('');
  
  // UI state
  loadingState = signal<{ isLoading: boolean; message: string }>({ isLoading: false, message: '' });
  openFilterModal = signal<boolean>(false);
  
  // Data state
  formEntries = signal<any[]>([]);
  currentColumns = signal<TableColumn[]>([]);
  advancedFilterColumns = signal<TableColumn[]>([]);

  // Track previous form type for cleanup
  private previousFormType: string = '';
  private isRouteNavigation: boolean = false;

  initialPaginationState: PaginationState = { 
    offset: 0,
    limit:50,
    totalCount: 0,
    hasMoreEntries: true
  };

  // Pagination state
  paginationState = signal<PaginationState>({
  ...this.initialPaginationState
  });
  
  // Filter/Sort state
  filterState = signal<FilterState>({
    column: '',
    value: '',
    isActive: false,
    filterType: 'equals'
  });
  
  sortState = signal<SortState>({
    column: '',
    direction: 'asc'
  });

  // ========================================
  // COMPUTED PROPERTIES
  // ========================================
  
  /**
   * Computed property that determines whether to show cards layout.
   * Returns true for small screens and phones, false for larger screens.
   * 
   * @returns True if cards layout should be shown
   */
  shouldShowCardsLayout = computed(() => {
    return this.screenSizeService.isSmallScreen() || this.screenSizeService.isPhone();
  });
  
  /**
   * Computed property that determines whether to show form entries.
   * Ensures that entries are only shown when appropriate filters are active.
   * 
   * @returns True if entries should be displayed
   */
  shouldShowEntries = computed(() => {
    const mode = this.filterMode();
    const formType = this.selectedFormType();
    const submitter = this.selectedSubmitter();
  
    if (mode === 'formType') return !!formType && !submitter;
    if (mode === 'submitter') return !!submitter && !formType;
    return false;
  });


  /** 
   * Computed property that determines if there are more entries available to load.
   * @returns true if the pagination state indicates more entries are available.
   */
  hasMoreEntries = computed(() => {
  const pagination = this.paginationState();

  return pagination.hasMoreEntries;
});

  
  /**
   * Computed property for the starting entry number on the current page.
   * Used for pagination display and user information.
   * 
   * @returns The starting entry number for the current page
   */
  entryStart = computed(() => {
    const pagination = this.paginationState();
    return pagination.totalCount === 0 ? 0 : pagination.offset + 1;
  });
  
  /**
   * Computed property for the ending entry number on the current page.
   * Used for pagination display and user information.
   * 
   * @returns The ending entry number for the current page
   */
  entryEnd = computed(() => {
    const pagination = this.paginationState();
    return Math.min(pagination.offset + pagination.limit, pagination.totalCount);
  });


  /**
   * Computed property that transforms advanced filter columns into a format
   * suitable for the filter modal interface.
   * 
   * @returns Array of filter column options with value and label
   */
  availableAdvancedFilterColumns = computed(() => {
    const columns = this.advancedFilterColumns();
    return Object.entries(columns).map(([key, label]) => ({
      value: key,
      label: label
    }));
  });

  // Static data
  readonly formTypes: FormMetadata[];
  readonly formTypeOptions: { id: string; title: string }[];
  
  readonly actionSheetFilterModeButtons = [
    {
      text: 'View All by Type',
      icon: 'albums-outline',
      handler: () => this.setFilterMode('formType')
    },
    {
      text: 'View All by Submitter', 
      icon: 'person-outline',
      handler: () => this.setFilterMode('submitter')
    },
    {
      text: 'Cancel',
      icon: 'close',
      role: 'cancel'
    }
  ];

  constructor(
    private formService: FormService,
    private formModelService: FormModelService,
    private router: Router,
    private route: ActivatedRoute,
    private screenSizeService: ScreenSizeService,
    private actionSheetController: ActionSheetController
  ) {
    this.formTypes = this.formModelService.getAllFormMetadata();
    this.formTypeOptions = this.formTypes.map(type => ({ id: type.formType, title: type.title }));
    
    this.setupEffects();
    this.setupRouteSubscription();
  }

  /**
   * Lifecycle hook that is called after data-bound properties are initialized.
   * Sets up the initial form type tracking and initializes from route parameters.
   */
  ngOnInit() {
    // Set the initial form type as previous for tracking
    this.previousFormType = this.selectedFormType();
    // this.initializeFromRoute();
  }

  // ========================================
  // REACTIVE EFFECTS AND SUBSCRIPTIONS
  // ========================================

  /**
   * Sets up reactive effects for automatic state synchronization.
   * Connects component state to service state changes.
   * 
   * @private
   */
  private setupEffects(): void {
    // Loading state effect
    effect(() => {
      const loadingState = this.formService.dataLoading();
      this.loadingState.set(loadingState);
    }, { allowSignalWrites: true });

    // Form entries effect
    effect(() => {
      const result = this.formService.formListResult();
      this.handleFormListResult(result);
    }, { allowSignalWrites: true });
  }

  /**
   * Sets up route parameter subscription for reactive navigation.
   * Handles query parameter changes and resolves conflicts between formId and submitter.
   * 
   * @private
   */
  private setupRouteSubscription(): void {
    this.route.queryParams.pipe(
      takeUntilDestroyed(),
      map(params => ({
        formId: params['formId'] || null,
        submitter: params['submitter'] || null
      })),
      distinctUntilChanged((prev, curr) => 
        prev.formId === curr.formId && prev.submitter === curr.submitter
      )
    ).subscribe(({ formId, submitter }) => {
      
      if (formId && submitter) {
        // Conflict resolution - prioritize formId
        this.router.navigate(['/form-entries'], {
          queryParams: { formId },
          replaceUrl: true
        });
        return;
      }
      
      if (formId) {
        this.handleRouteFormId(formId);
      } else if (submitter) {
        this.handleRouteSubmitter(submitter);
      } else {
        this.clearAllFilters();
      }
    });
  }

  // ========================================
  // ROUTE HANDLING
  // ========================================

  /**
   * Handles route changes for form type filtering.
   * Updates component state and loads appropriate data for the selected form type.
   * 
   * @param formId - The form type identifier from the route
   * @private
   */
  private handleRouteFormId(formId: string): void {
    
    // Mark this as route navigation
    this.isRouteNavigation = true;
    
    // Store previous form type before changing
    this.previousFormType = this.selectedFormType();
    
    this.filterMode.set('formType');
    this.selectedFormType.set(formId);
    this.selectedSubmitter.set('');
    this.resetPaginationAndFilters();

    // Update advanced filter columns
    this.updateAdvancedFilterColumns(formId);
    
    // Only load from localStorage during route navigation

    
    // Load data
    this.loadFormDataByType(formId);
    
    // Reset route navigation flag
    this.isRouteNavigation = false;
  }

  /**
   * Handles route changes for submitter filtering.
   * Updates component state and loads data for the specified submitter.
   * 
   * @param submitter - The submitter name from the route
   * @private
   */
  private handleRouteSubmitter(submitter: string): void {
    
    // Mark this as route navigation
    this.isRouteNavigation = true;
    
    this.filterMode.set('submitter');
    this.selectedSubmitter.set(submitter);
    this.selectedFormType.set('');
    this.resetPaginationAndFilters();
    

    
    // Load data
    this.loadFormDataBySubmitter(submitter);
    
    // Reset route navigation flag
    this.isRouteNavigation = false;
  }

  // /**
  //  * Initializes component state from route parameters on component initialization.
  //  * Sets up the initial filter state based on URL query parameters.
  //  * 
  //  * @private
  //  */
  // private initializeFromRoute(): void {
  //   const params = this.route.snapshot.queryParams;
  //   const formId = params['formId'];
  //   const submitter = params['submitter'];
    
  //   if (formId) {
  //     console.log('there is a form id so we are running handleRouteFormID', formId);
  //     this.handleRouteFormId(formId);
  //   } else if (submitter) {
  //     this.handleRouteSubmitter(submitter);
  //   }
  // }

  // ========================================
  // DATA LOADING
  // ========================================

  /**
   * Loads form data filtered by form type.
   * Sets up columns, applies current filters and sorting, and fetches data from the service.
   * 
   * @param formType - The form type to load data for
   * @private
   */
  private loadFormDataByType(formType: string): void {
    const formMetadata = this.formModelService.getFormMetadata(formType);
    if (!formMetadata) {
      console.error('Form type not found:', formType);
      return;
    }

    
    this.formService.formEntryType.set(formType);
    this.currentColumns.set(formMetadata.tableColumns);
    
    const pagination = this.paginationState();
    const filterState = this.filterState();
    const sortState = this.sortState();
    this.formService.fetchInitialFormEntriesByType<any>(
      formType,
      0, // Always start from offset 0 for initial load
      pagination.limit,
      sortState.column || undefined,
      sortState.direction || undefined,
      filterState.isActive ? filterState.column : undefined,
      filterState.isActive ? filterState.value : undefined,
      filterState.isActive ? filterState.filterType : undefined,

    );
  }

  /**
   * Loads form data filtered by submitter name.
   * Sets up columns for submitter view and fetches data from the service.
   * 
   * @param submitter - The submitter name to filter by
   * @private
   */
  private loadFormDataBySubmitter(submitter: string): void {
    
    this.currentColumns.set(AllFormsBySubmitterListItem.getTableColumns());
    
    const pagination = this.paginationState();
    const filterState = this.filterState();
    const sortState = this.sortState();
    
    this.formService.fetchInitialFormEntriesBySubmitter<AllFormsBySubmitterListItem>(
      submitter,
      0, // Always start from offset 0 for initial load
      pagination.limit,
      sortState.column || undefined,
      sortState.direction || undefined,
      filterState.isActive ? filterState.column : undefined,
      filterState.isActive ? filterState.value : undefined,
      filterState.isActive ? filterState.filterType : undefined,

    );
  }

  /**
   * Handles the form list result from the service.
   * Since service now only provides latest entries, component handles appending logic.
   * 
   * @param result - The form list response from the service
   * @private
   */
private handleFormListResult(result: FormListResponse<any> | null): void {
  if (!result) {
    this.formEntries.set([]);
    this.resetPagination();
    return;
  }

  // Transform docs depending on filter mode
  let newEntries: any[] = [];
  if (result?.entries?.docs) {
    if (this.filterMode() === 'submitter') {
      newEntries = result.entries.docs.map((doc: any) => ({
        id: doc.id,
        formType: doc.formType,
        formEntryData: AllFormsBySubmitterListItem.fromApiResponse(doc)
      }));
    } else {
      newEntries = result.entries.docs;
    }
  }

  const newEntryCount = result.entries.count || 0;
  const currentPagination = untracked(this.paginationState);
  const currentEntries = untracked(this.formEntries);
  const currentTotalCount = currentPagination.totalCount || 0;
  const currentLimit = currentPagination.limit || 0;
  const currentOffset = currentPagination.offset || 0;
  // Determine if this is initial load or additional entries
  const isInitialLoad = currentOffset === 0 || currentEntries.length === 0;
  
  // Case 1: service gave us no extra entries → we reached the end
  if (newEntryCount===0) {
    this.paginationState.update(state => ({
      ...state,
      hasMoreEntries: false
    }));
    return; // don’t set entries again
  }

  // Replace with service’s current list (already appended upstream)
  this.formEntries.set([...currentEntries, ...newEntries]);

  // Case 2: fewer than limit came back or we’ve hit total count
  const reachedEnd = newEntryCount< currentLimit;
  const newTotalCount = newEntryCount+ currentTotalCount;

  this.paginationState.update(state => ({
    ...state,
    totalCount: newTotalCount,
    hasMoreEntries: !reachedEnd
  }));
}

  // ========================================
  // STATE MANAGEMENT HELPERS
  // ========================================

  /**
   * Resets both pagination and filter states to their initial values.
   * Used when changing filter modes or form types.
   * 
   * @private
   */
  private resetPaginationAndFilters(): void {
    this.resetPagination();
    this.resetFilters();
  }

  /**
   * Resets pagination state to the first page with default page size.
   * 
   * @private
   */
  private resetPagination(): void {
    this.paginationState.set({
      ...this.initialPaginationState
    });
  }

  /**
   * Resets filter and sort states to their default values.
   * Sets default sorting to submission date in descending order.
   * 
   * @private
   */
  private resetFilters(): void {
    this.filterState.set({
      column: '',
      value: '',
      isActive: false,
      filterType: 'equals'
    });
    
    this.sortState.set({
      column: 'submissionDate',
      direction: 'desc'
    });
  }


  /**
   * Clears all filters and resets component state to initial values.
   * Used when no specific filters are active.
   * 
   * @private
   */
  private clearAllFilters(): void {
    this.selectedFormType.set('');
    this.selectedSubmitter.set('');
    this.formEntries.set([]);
    this.currentColumns.set([]);
    this.advancedFilterColumns.set([]);
    this.resetPaginationAndFilters();
    if (!this.shouldShowCardsLayout()) {
      this.dynamicTable.clearFiltersLocally(); 
    }
    
  }


  /**
   * Updates the advanced filter columns based on the selected form type.
   * Dynamically generates filter columns from form metadata, excluding upload and signature fields.
   * 
   * @param formType - The form type to generate filter columns for
   * @private
   */
  private updateAdvancedFilterColumns(formType: string): void {
    const formMetadata = this.formModelService.getFormMetadata(formType);
  
    if (!formMetadata || !formMetadata.fieldMappings || !formMetadata.fieldMetadata) {
      this.advancedFilterColumns.set([]);
      return;
    }
  
    const filterColumns: TableColumn[] = [];
    
    // Iterate through field mappings
    Object.entries(formMetadata.fieldMappings).forEach(([fieldKey, fieldLabel]) => {
      const fieldMeta = formMetadata.fieldMetadata![fieldKey];
      
      // Only include fields that are NOT upload or signature arrays
      if (fieldMeta && !fieldMeta.isUploadArray && !fieldMeta.isSignatureArray) {
        // Determine column type based on field metadata
        let columnType: TableColumn['type'] = 'text';
        
        if (fieldMeta.isDate) {
          columnType = 'date';
        } else if (fieldMeta.isNumber) {
          columnType = 'number';
        } else if (fieldKey.toLowerCase().includes('status')) {
          columnType = 'status';
        }
        
        // Create TableColumn object
        const column: TableColumn = {
          key: fieldKey,
          label: fieldLabel,
          type: columnType,
          sortable: true
        };
        
        filterColumns.push(column);
      }
    });
    
    this.advancedFilterColumns.set(filterColumns);
  }

  // ========================================
  // EVENT HANDLERS
  // ========================================

  /**
   * Presents the filter mode selection action sheet.
   * Allows users to switch between form type and submitter filtering modes.
   * 
   * @async
   */
  async presentFilterModeActionSheet(): Promise<void> {
    const actionSheet = await this.actionSheetController.create({
      header: 'Filter Mode',
      buttons: this.actionSheetFilterModeButtons
    });
    await actionSheet.present();
  }

  /**
   * Sets the filter mode and resets all related state.
   * Clears current selections and navigates to the base form entries route.
   * 
   * @param mode - The filter mode to set ('formType' or 'submitter')
   */
  setFilterMode(mode: FilterMode): void {
  
    
    this.filterMode.set(mode);
  
    if (mode === 'formType') {
      this.selectedSubmitter.set('');
      this.selectedFormType.set('');
    } else {
      this.selectedFormType.set('');
      this.selectedSubmitter.set('');
    }
  
    this.formEntries.set([]);
    this.currentColumns.set([]);
    this.advancedFilterColumns.set([]);
    this.resetPaginationAndFilters();
    this.previousFormType = '';
  
    this.router.navigate(['/form-entries'], {
      queryParams: {},
      replaceUrl: true
    });
  }

  /**
   * Handles form type selection changes.
   * Updates the selected form type, refreshes filter columns, and navigates to the new route.
   * 
   * @param newFormType - The newly selected form type
   */
  onFormTypeChange(newFormType: string): void {
    const currentFormType = this.selectedFormType(); // Get current before setting new
    
    
   
    // Update the selected form type
    this.selectedFormType.set(newFormType);
    
    if (newFormType) {
      // Store this as the new previous form type
      this.previousFormType = newFormType;
      
      // Update field filter columns first
      this.updateAdvancedFilterColumns(newFormType);

      // Reset filter and sort states (don't load from localStorage unless it's route navigation)
      this.resetFilters();

      this.formService.formEntryType.set(newFormType);
      this.router.navigate(['/form-entries'], {
        queryParams: { formId: newFormType },
        replaceUrl: true
      });
    }
  }



  /**
   * Handles submitter search submission.
   * Navigates to the form entries route with the submitter query parameter.
   */
  onSubmitterSearch(): void {
    const submitter = this.selectedSubmitter();
    
    if (submitter?.trim()) {
      this.router.navigate(['/form-entries'], {
        queryParams: { submitter },
        replaceUrl: true
      });
    }
  }

  /**
   * Handles sorting events from the table or filter components.
   * Updates the sort state and reloads data with the new sorting applied.
   * 
   * @param event - Object containing column, direction, and advanced filter flag
   */
  onSort(event: { column: string, direction: 'asc' | 'desc', advancedFilter: boolean }): void {
    this.sortState.set({
      column: event.column,
      direction: event.direction
    });

  
    
    // Explicitly reload data with new sort
    this.reloadCurrentData();
  }

  /**
   * Handles filter events from the table or filter components.
   * Updates the filter state and reloads data with the new filter applied.
   * 
   * @param event - Object containing column, value, advanced filter flag, and operation
   */
  onFilter(event: { column: string, value: string, advancedFilter: boolean, operation: 'equals' | 'contains' | 'greaterThan' | 'lessThan' }): void {
    if (!event.column || !event.value.trim()) return;
  
    if (event.advancedFilter && !this.shouldShowCardsLayout()) {
      this.dynamicTable.clearFiltersLocally(); 
    }

    this.filterState.set({
      column: event.column,
      value: event.value,
      isActive: true,
      filterType: event.operation
    });
    
    // Reset pagination to start from beginning when applying new filter
    this.paginationState.update(state => ({ ...state, offset: 0 }));
    
    // Explicitly reload data with new filter
    this.reloadCurrentData();
  }

 

  /**
   * Clears the current filter and reloads data.
   * Resets filter state and clears local table filters if applicable.
   */
  onClearFilter(): void {
    this.filterState.set({
      column: '',
      value: '',
      isActive: false,
      filterType: 'equals'
    });

    if (!this.shouldShowCardsLayout()) {
      this.dynamicTable.clearFiltersLocally(); 
    }
    
    // Reset pagination to start from beginning when clearing filter
    this.paginationState.update(state => ({ ...state, offset: 0 }));
    
    // Explicitly reload data without filter
    this.reloadCurrentData();
  }

 

  /**
   * Clears the current sort and reloads data.
   * Resets sort state and clears local table sorting if applicable.
   */
  onClearSort(): void {
    this.sortState.set({
      column: '',
      direction: 'asc'
    });
    
    if (!this.shouldShowCardsLayout()) {
      this.dynamicTable.clearSortLocally(); 
    }
    
    // Explicitly reload data without sort
    this.reloadCurrentData();
  }


  /**
   * Helper method to reload data based on current filter and sort state.
   * Determines the appropriate data loading method based on the current filter mode.
   * 
   * @private
   */
  private reloadCurrentData(): void {
    const mode = this.filterMode();
    const formType = this.selectedFormType();
    const submitter = this.selectedSubmitter();
    
    if (mode === 'formType' && formType) {
      this.loadFormDataByType(formType);
    } else if (mode === 'submitter' && submitter) {
      this.loadFormDataBySubmitter(submitter);
    }
  }

  /**
   * Navigates to the form view component for a specific entry.
   * Routes to the form view page with the entry ID.
   * 
   * @param entry - The form entry object containing the ID
   */
  onViewEntry(entry: any): void {
    this.router.navigate(['/form-view', entry.id]);
  }

  /**
   * Creates a new form of the currently selected type.
   * Navigates to the form creation page for the selected form type.
   */
  onCreateNewForm(): void {
    const formType = this.selectedFormType();
    if (this.filterMode() === 'formType' && formType) {
      const formMetadata = this.formModelService.getFormMetadata(formType);
      if (formMetadata) {
        this.router.navigate(['/forms', formMetadata.formType]);
      }
    }
  }

  /**
   * Handles the "Load More" button click.
   * Increases the offset and fetches additional entries using the offset-based service method.
   */
  onLoadMore(): void {
    const currentPagination = this.paginationState();
    const newOffset = currentPagination.offset + currentPagination.limit;
    
    this.paginationState.update(state => ({
      ...state,
      offset: newOffset
    }));

    const mode = this.filterMode();
    const formType = this.selectedFormType();
    const submitter = this.selectedSubmitter();
    const filterState = this.filterState();
    const sortState = this.sortState();

    if (mode === 'formType' && formType) {
      this.formService.fetchOffsetEntryListByType<any>(
        formType,
        newOffset,
        currentPagination.limit,
        sortState.column || undefined,
        sortState.direction || undefined,
        filterState.isActive ? filterState.column : undefined,
        filterState.isActive ? filterState.value : undefined,
        filterState.isActive ? filterState.filterType : undefined
      );
    } else if (mode === 'submitter' && submitter) {
      this.formService.fetchOffsetEntryListBySubmitter<AllFormsBySubmitterListItem>(
        submitter,
        newOffset,
        currentPagination.limit,
        sortState.column || undefined,
        sortState.direction || undefined,
        filterState.isActive ? filterState.column : undefined,
        filterState.isActive ? filterState.value : undefined,
        filterState.isActive ? filterState.filterType : undefined
      );
    }
  }

  // Modal handlers
  /**
   * Opens the filter and sort modal for advanced filtering options.
   */
  onOpenFilterModal(): void {
   this.filterSortModal.present();
  }

  /**
   * Closes the filter and sort modal.
   */
  onCloseFilterModal(): void {
    this.filterSortModal.dismiss();
  }

 

  // Getters for template compatibility
  /**
   * Getter for template compatibility with the screen size service.
   * 
   * @returns True if the current screen is small
   */
  get isSmallScreen(): boolean {
    return this.screenSizeService.isSmallScreen();
  }

  /**
   * Checks if there are active filters applied.
   * Used to show visual indicators of active filtering.
   * 
   * @returns True if there are active filters
   */
  hasActiveFilters(): boolean {
    return this.filterState().isActive ;
  }

}